/**
 * Un avis — et le moyen de s'en débarrasser.
 *
 * **Un message qu'on ne peut pas fermer finit par ne plus être lu.** La
 * plateforme en pose beaucoup : ce qui manque avant de chiffrer, ce qu'un
 * rechargement a trouvé, ce qu'un instantané garantit. Tous sont justes, et
 * tous deviennent du décor s'ils restent après avoir été compris — au point
 * qu'on cesse de voir celui qui, ce jour-là, disait quelque chose de neuf.
 *
 * **Fermer n'est pas oublier.** L'avis se referme pour cette lecture ; il
 * revient si la condition qui l'a produit revient. Un manque comblé ne
 * reparaît pas ; un manque toujours là reparaît au prochain chargement, et
 * c'est ce qu'on veut.
 *
 * `permanent` existe pour les rares cas où la fermeture serait un piège : un
 * refus qui explique pourquoi un bouton ne répond pas doit rester tant que le
 * bouton ne répond pas.
 */
import { useEffect, useState } from "react";

type Ton = "info" | "attention" | "erreur" | "verrou" | "neutre";

export function Avis({
  ton = "neutre",
  permanent = false,
  cle,
  className = "",
  children,
}: {
  ton?: Ton;
  permanent?: boolean;
  /** Ce qui identifie le message. **Changer de clé rouvre l'avis** : sans
   *  cela, fermer « déjà à jour » masquerait le « nouvelle version » suivant,
   *  et le métreur attendrait un import qu'il croirait lancé. */
  cle?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const [ferme, poserFerme] = useState(false);

  useEffect(() => {
    poserFerme(false);
  }, [cle]);

  if (ferme) return null;

  const teinte = ton === "neutre" ? "" : ` avis--${ton}`;
  // Une erreur se dit **tout de suite** : `alert` interrompt le lecteur
  // d'écran, là où `status` attend une pause. Les autres tons restent polis.
  const role = ton === "erreur" ? "alert" : "status";
  return (
    <div className={`avis${teinte} ${className}`.trim()} role={role}>
      <div className="avis__corps">{children}</div>
      {!permanent && (
        <button
          type="button"
          className="avis__fermer"
          aria-label="Fermer ce message"
          title="Fermer"
          onClick={() => poserFerme(true)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
      )}
    </div>
  );
}
